import {
  BaseQueryApi,
  createApi,
  FetchArgs,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { Hauler } from "src/db/Hauler"
import { HaulerEmployee } from "src/db/HaulerEmployee"
import { Order } from "src/db/Order"
import { OrderDetail } from "src/db/OrderDetail"
import { OrderCardLayoutConfig } from "src/features/config-setting/models"
import {
  apiConstants,
  apiPathConstants,
  InvalidateTagsType,
} from "src/features/shared-module/constants"
import { RouteSummary } from "src/features/shared-module/models"
import { getAuth0 } from "src/utils/auth0Utils"

// const BASE_URL = import.meta.env.VITE_BASE_URL
const BASE_URL = import.meta.env.VITE_MBLS_DISPATCH_URL

// Backend slonik interceptor already applies toCamelCase to every row,
// so no client-side conversion needed — just extract .data
const transformData = <T>(response: { data: T }): T => {
  return response.data
}

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  // content type json,
  prepareHeaders: async (headers) => {
    headers.set("Content-Type", "application/json")
    headers.set("ts", Date.now().toString())
    try {
      const auth0 = getAuth0()
      const token = await auth0.getAccessTokenSilently({
        authorizationParams: {
          audience: "mbls-global-api",
        },
      })
      if (token) {
        headers.set("Authorization", `Bearer ${token}`)
      }
    } catch (error) {
      console.error("Error fetching Auth0 token:", error)
    }
    return headers
  },
})

export const baseQueryCamelCaseHandler = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  options: {} = {},
) => {
  const result = await baseQuery(args, api, options)

  // Log dev trace IDs so Playwright can read them from console
  if (import.meta.env.DEV) {
    const meta = result.meta as any
    const resp = meta?.response as Response | undefined
    const traceId = resp?.headers?.get("x-mbls-dev-trace-id")
    if (traceId) {
      const url =
        meta?.request?.url ?? (typeof args === "string" ? args : args.url)
      console.log(`[devtrace] ${url} ${traceId}`)
    }
  }

  if (result?.error?.status === 401) {
    try {
      const auth0 = getAuth0()
      await auth0.logout({
        logoutParams: {
          returnTo: window.location.origin + "/login?reason=unauthorized",
        },
      })
    } catch (e) {
      window.location.href = "/login?reason=unauthorized"
    }
  }

  return result
}

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryCamelCaseHandler,
  tagTypes: ["Route Planning", "Orders"] as InvalidateTagsType[],
  endpoints: (builder) => ({
    getIotCreds: builder.query<any, void>({
      query: () => ({ url: "/iot/creds" }),
    }),
    getOrderDetailById: builder.query<OrderDetail, number>({
      query: (orderId: number) => ({
        url: apiConstants.GET_ORDER_DETAIL_BY_ID.replace(
          `:${apiPathConstants.orderId}`,
          orderId.toString(),
        ),
      }),
      transformResponse: transformData<OrderDetail>,
    }),
    getOrdersByDate: builder.query<
      { data: Order[]; total: number; hasMore: boolean },
      { date: string; limit?: number; offset?: number }
    >({
      query: ({ date, limit = 200, offset = 0 }) => ({
        url: apiConstants.GET_ORDER_LIST,
        params: { date, limit, offset },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((order) => ({
                type: "Orders" as const,
                id: order.id,
              })),
              { type: "Orders", id: "LIST" },
            ]
          : [{ type: "Orders", id: "LIST" }],
    }),
    getAllHaulers: builder.query<any, any>({
      query: () => ({
        url: apiConstants.GET_PROVIDER_LIST,
      }),
      transformResponse: transformData<Hauler[]>,
    }),
    getAllEmployees: builder.query<HaulerEmployee[], any>({
      query: () => ({
        url: apiConstants.GET_HAULER_EMP_LIST,
      }),
      transformResponse: transformData<HaulerEmployee[]>,
    }),
    getOrderPositions: builder.query<
      Array<{
        id: number
        state: string
        haulerEmployeeId: number | null
        routeSummaryId: number | null
        latitude: number | null
        longitude: number | null
        reference: string
        sequence: number | null
      }>,
      string
    >({
      query: (date) => ({
        url: "/orders/positions",
        params: { date },
      }),
      transformResponse: transformData<any>,
      providesTags: [{ type: "Orders", id: "LIST" }],
    }),
    getOrderRouteById: builder.query<{ data: RouteSummary }, number>({
      query: (routeSummaryId: number) => ({
        url: apiConstants.GET_ROUTE_SUMMARY.replace(
          `:${apiPathConstants.routeSummaryId}`,
          routeSummaryId.toString(),
        ),
      }),
    }),
    createOrUpdateOrderLayout: builder.mutation<
      { data: OrderCardLayoutConfig },
      OrderCardLayoutConfig
    >({
      query: (params: OrderCardLayoutConfig) => ({
        url: apiConstants.UPSERT_SETTINGS.replace(
          `:${apiPathConstants.userId}`,
          (params.id as number).toString(),
        ),
        method: "PUT",
        body: params,
      }),
    }),
    getOrderLayoutConfig: builder.query<
      { data: OrderCardLayoutConfig },
      number
    >({
      query: (userId: number) => ({
        url: apiConstants.UPSERT_SETTINGS.replace(
          `:${apiPathConstants.userId}`,
          userId.toString(),
        ),
      }),
      transformErrorResponse: () => {
        return null
      },
    }),
    assignOrderToEmployee: builder.mutation<
      { ok: boolean; previousState: string; newState: string },
      { orderId: number; haulerEmployeeId: number }
    >({
      query: ({ orderId, haulerEmployeeId }) => ({
        url: `/orders/${orderId}/assign`,
        method: "POST",
        body: { haulerEmployeeId },
      }),
    }),
    batchAssignOrders: builder.mutation<
      {
        results: Array<{
          orderId: number
          ok: boolean
          previousState?: string
          newState?: string
          error?: string
        }>
        summary: { total: number; success: number; failed: number }
      },
      { assignments: Array<{ orderId: number; haulerEmployeeId: number }> }
    >({
      query: ({ assignments }) => ({
        url: `/orders/assign`,
        method: "POST",
        body: { assignments },
      }),
    }),
  }),
})

export const {
  useGetIotCredsQuery,
  useGetOrderDetailByIdQuery,
  useGetOrdersByDateQuery,
  useGetOrderPositionsQuery,
  useGetAllEmployeesQuery,
  useGetAllHaulersQuery,
  useGetOrderRouteByIdQuery,
  useLazyGetOrdersByDateQuery,
  useLazyGetOrderRouteByIdQuery,
  useCreateOrUpdateOrderLayoutMutation,
  useGetOrderLayoutConfigQuery,
  useLazyGetOrderLayoutConfigQuery,
  useAssignOrderToEmployeeMutation,
  useBatchAssignOrdersMutation,
} = baseApi
